
import { UseFormReturn } from "react-hook-form";
import { TrainingFormValues } from "../../../types";
import { FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Video, CheckCircle, AlertCircle, Info } from "lucide-react";
import { useState, useEffect } from "react";
import { validateVideoUrl, getVideoUrlExamples, getSupportedPlatforms } from "../utils/videoUrlValidation";

interface YouTubeUrlSectionProps {
  form: UseFormReturn<TrainingFormValues>;
}

export function YouTubeUrlSection({ form }: YouTubeUrlSectionProps) {
  const [validationState, setValidationState] = useState<{
    isValid: boolean;
    provider?: string;
    errorMessage?: string;
  }>({ isValid: true });

  const youtubeUrl = form.watch('youtube_url');

  useEffect(() => {
    if (youtubeUrl) {
      const result = validateVideoUrl(youtubeUrl);
      setValidationState({
        isValid: result.isValid,
        provider: result.provider,
        errorMessage: result.errorMessage
      });
    } else {
      setValidationState({ isValid: true });
    }
  }, [youtubeUrl]);

  const getValidationIcon = () => {
    if (!youtubeUrl) return <Video className="h-4 w-4 text-muted-foreground" />;
    if (validationState.isValid) return <CheckCircle className="h-4 w-4 text-green-500" />;
    return <AlertCircle className="h-4 w-4 text-red-500" />;
  };

  const getValidationMessage = () => {
    if (!youtubeUrl) return null;
    if (validationState.isValid && validationState.provider) {
      return (
        <div className="flex items-center gap-1 text-sm text-green-600">
          <CheckCircle className="h-3 w-3" />
          Valid {validationState.provider === 'youtube' ? 'YouTube' : validationState.provider === 'vimeo' ? 'Vimeo' : 'video'} URL
        </div>
      );
    }
    if (!validationState.isValid) {
      return (
        <div className="text-sm text-red-600 whitespace-pre-line">
          {validationState.errorMessage}
        </div>
      );
    }
    return null;
  };

  return (
    <FormField
      control={form.control}
      name="youtube_url"
      render={({ field }) => (
        <FormItem>
          <div className="flex items-center gap-2">
            {getValidationIcon()}
            <FormLabel>Video URL</FormLabel>
          </div>
          <FormControl>
            <Input
              placeholder={getVideoUrlExamples()[0]}
              className={
                youtubeUrl 
                  ? validationState.isValid 
                    ? "border-green-300 focus:border-green-500" 
                    : "border-red-300 focus:border-red-500"
                  : ""
              }
              {...field}
            />
          </FormControl>
          
          {getValidationMessage()}
          
          <FormDescription>
            <span className="flex items-center gap-1">
              <Info className="h-3 w-3" />
              <span>Supported platforms: {getSupportedPlatforms().join(', ')}</span>
            </span>
            <span className="block text-xs text-muted-foreground mt-1">
              Examples: {getVideoUrlExamples().slice(0, 2).join(' • ')}
            </span>
          </FormDescription>
          <FormMessage />
        </FormItem>
      )}
    />
  );
}
